/* astro-ephemeris.js — эфемериды для тулов, которым нужна ТОЧНОСТЬ.
 *
 * ⚠️ РЯДОМ ЛЕЖИТ astro-core.js — ЭТО ДРУГОЙ МОДУЛЬ, НЕ ПУТАТЬ.
 * ----------------------------------------------------------
 * `astro-core.js` (window.AstroCore) — положение Солнца и Луны по SunCalc,
 * углы в РАДИАНАХ; им живут /compass, /sun-moon-tracker и earth-globe. Его
 * задача — «куда смотреть», и ±0.3° там ничего не решают.
 * Этот файл (window.AstroEphemeris) — углы в ГРАДУСАХ, счёт ведёт движок,
 * проверенный против JPL: фазы, либрация, перигей/апогей, полосы затмений.
 * Две задачи с разной ценой ошибки, поэтому два модуля, а не один.
 * Оба имени в глобале одновременно — намеренно; страница может грузить любой.
 * Если когда-нибудь сводить их в один — сводить ОСОЗНАННО (у SunCalc-ветки
 * свой контракт в радианах и свои вызовы), а не потому что имена похожи.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ СУЩЕСТВУЕТ
 * --------------------------
 * До него на сайте жили ТРИ разные Луны: earth-globe-astro.js, sun-moon-tracker.js
 * и meteor-showers.js — каждый со своей усечённой копией формул Меёса. У
 * sun-moon-tracker в долготе оставлен один член (6.289°), это ±0.3°: для восхода
 * нормально, для затмения — нет, а либрации нет нигде. Три копии расходятся
 * молча: «здесь полнолуние, а там затмение не нашлось» — и никто не виноват.
 *
 * Поэтому счёт ведёт ОДИН движок — вендоренный astronomy-engine (MIT, Don Cross,
 * public/vendor/astronomy-engine/2.1.19). Он проверен против эфемерид JPL; наша
 * сверка против опубликованных фактов лежит в tests/js/astro-ephemeris.test.mjs:
 * пик затмения 08.04.2024 сходится до 3 секунд, полная фаза в Луксоре
 * 02.08.2027 — до 3 секунд, либрация и перигей/апогей — до сотен километров.
 *
 * ЧТО ДОБАВЛЯЕТ ЭТОТ МОДУЛЬ СВЕРХ ДВИЖКА
 * --------------------------------------
 * Движок даёт физику. Здесь — то, чего в нём нет и что нужно людям:
 *   - ЛУННЫЙ ДЕНЬ по правильному определению: лунные сутки меняются в МОМЕНТ
 *     ВОСХОДА ЛУНЫ, а не в полночь. Русские лунные календари почти поголовно
 *     печатают таблицу «на Москву» и выдают её за истину для всей страны —
 *     во Владивостоке она врёт на сутки. Мы считаем от восхода В ТОЧКЕ
 *     ПОЛЬЗОВАТЕЛЯ, поэтому у нас лунный день бывает 1-й и 2-й в один день —
 *     как и должно быть.
 *   - УГОЛ СВЕТЛОГО КРАЯ + параллактический угол: серп на экране повёрнут ровно
 *     так, как человек видит его со своего балкона. Все рисуют «рожки вбок».
 *   - СУПЕРЛУНИЕ/МИКРОЛУНИЕ — сравнением с ближайшими перигеем/апогеем, а не по
 *     фиксированному порогу километров.
 *   - Знак зодиака Луны — из эклиптической долготы, а не из таблицы.
 *
 * Углы наружу — в градусах, время — Date (UTC внутри). Модуль не знает про DOM,
 * не знает про язык и тестируется в node.
 */
(function (root, factory) {
  var Engine = (typeof Astronomy !== 'undefined') ? Astronomy
    : (typeof root !== 'undefined' && root.Astronomy) ? root.Astronomy
    : null;
  var api = factory(Engine);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.AstroEphemeris = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function (A) {
  'use strict';

  var DAY_MS = 86400000;
  var MOON_RADIUS_KM = 1737.4;

  // Движок может приехать позже (защищённый скрипт, отложенная загрузка).
  // Не падаем на разборе файла — падаем на вызове, с внятным текстом.
  function engine() {
    var e = A || (typeof Astronomy !== 'undefined' ? Astronomy : null) ||
      (typeof globalThis !== 'undefined' ? globalThis.Astronomy : null);
    if (!e) throw new Error('astro-ephemeris: astronomy-engine не загружен');
    return e;
  }

  function deg(x) { return x * 180 / Math.PI; }
  function rad(x) { return x * Math.PI / 180; }
  function norm360(x) { x = x % 360; return x < 0 ? x + 360 : x; }

  /** Наблюдатель движка из простых чисел. Высота в метрах, по умолчанию 0. */
  function observer(lat, lon, elevM) {
    return new (engine().Observer)(lat, lon, elevM || 0);
  }

  // ── Фазы ────────────────────────────────────────────────────────────────────

  // Восемь фаз. Границы — не «по 45°», а как их называют люди: четверти это
  // МОМЕНТЫ, поэтому вокруг них узкие окна, а серпы/горбатые — широкие.
  var PHASE_KEYS = ['new', 'waxing_crescent', 'first_quarter', 'waxing_gibbous',
    'full', 'waning_gibbous', 'last_quarter', 'waning_crescent'];

  /**
   * Индекс фазы 0..7 по углу фазы Луны (0° = новолуние, 180° = полнолуние).
   * @param {number} phaseDeg 0..360, растёт от новолуния
   */
  function phaseIndex(phaseDeg) {
    var p = norm360(phaseDeg);
    if (p < 1.5 || p >= 358.5) return 0;      // новолуние
    if (p < 88.5) return 1;                    // растущий серп
    if (p < 91.5) return 2;                    // первая четверть
    if (p < 178.5) return 3;                   // растущая (горбатая)
    if (p < 181.5) return 4;                   // полнолуние
    if (p < 268.5) return 5;                   // убывающая (горбатая)
    if (p < 271.5) return 6;                   // последняя четверть
    return 7;                                  // убывающий серп
  }

  /** Ближайшие n смен фаз начиная с date → [{quarter 0..3, date}] */
  function nextQuarters(date, n) {
    var E = engine(), out = [], q = E.SearchMoonQuarter(date);
    for (var i = 0; i < (n || 4); i++) { out.push({ quarter: q.quarter, date: q.time.date }); q = E.NextMoonQuarter(q); }
    return out;
  }

  /** Момент ПРЕДЫДУЩЕГО новолуния (нужен для возраста и лунных суток). */
  function previousNewMoon(date) {
    var E = engine();
    // Ищем от «30.5 суток назад» — синодический месяц 29.53, запас гарантирует
    // попадание; берём последнее новолуние, которое ещё не позже date.
    var t = new Date(date.getTime() - 31 * DAY_MS);
    var q = E.SearchMoonQuarter(t), last = null;
    for (var i = 0; i < 10; i++) {
      if (q.quarter === 0) {
        if (q.time.date.getTime() <= date.getTime()) last = q.time.date; else break;
      }
      q = E.NextMoonQuarter(q);
    }
    return last;
  }

  // ── Лунные сутки ────────────────────────────────────────────────────────────

  /**
   * Лунный день в точке наблюдателя.
   *
   * Определение (классическое, его же используют лунные календари, только они
   * считают его для одной точки и печатают для всей страны):
   *   1-е лунные сутки  — от МОМЕНТА НОВОЛУНИЯ до первого восхода Луны после него
   *                       (поэтому они почти всегда короткие, иногда минуты);
   *   N-е лунные сутки  — от восхода Луны до следующего восхода.
   *
   * Возвращает { day, start, end, lengthMin } или null, если в приполярных
   * широтах Луна не восходила — там суток просто нет, и врать об этом нельзя.
   */
  function lunarDay(date, obs) {
    var E = engine();
    var newMoon = previousNewMoon(date);
    if (!newMoon) return null;

    // Восходы Луны от новолуния. Синодический месяц ≤ 30 суток, восходов ≤ 31.
    var rises = [], cursor = new Date(newMoon.getTime());
    var guard = new Date(date.getTime() + 2 * DAY_MS);
    for (var i = 0; i < 34; i++) {
      var r = E.SearchRiseSet('Moon', obs, +1, cursor, 2);
      if (!r) { // приполярная дыра: перепрыгиваем сутки и пробуем дальше
        cursor = new Date(cursor.getTime() + DAY_MS);
        if (cursor > guard) break;
        continue;
      }
      if (r.date > guard) break;
      rises.push(r.date);
      cursor = new Date(r.date.getTime() + 3600000); // +1 ч, чтобы не найти тот же
    }
    if (!rises.length) return null;

    if (date < rises[0]) {
      return { day: 1, start: newMoon, end: rises[0],
        lengthMin: Math.round((rises[0] - newMoon) / 60000) };
    }
    for (var k = rises.length - 1; k >= 0; k--) {
      if (date >= rises[k]) {
        var end = rises[k + 1] || null;
        return { day: k + 2, start: rises[k], end: end,
          lengthMin: end ? Math.round((end - rises[k]) / 60000) : null };
      }
    }
    return null;
  }

  // ── Зодиак ──────────────────────────────────────────────────────────────────

  var ZODIAC = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
    'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];

  /** Знак зодиака по эклиптической долготе (тропический, от точки овна). */
  function zodiacOf(eclipticLonDeg) {
    var l = norm360(eclipticLonDeg);
    var i = Math.floor(l / 30);
    return { key: ZODIAC[i], index: i, degInSign: l - i * 30 };
  }

  // ── Ориентация серпа ────────────────────────────────────────────────────────

  /**
   * Позиционный угол светлого края (bright limb) и параллактический угол.
   *
   * Зачем: на всех сайтах Луна нарисована «рожками вбок», как в учебнике. В небе
   * она повёрнута иначе — и поворот зависит от того, ГДЕ стоит человек и КОГДА
   * смотрит. Разница между картинкой и небом — главная причина «у вас неправильная
   * фаза». Считаем оба угла и даём тулу повернуть диск как в реальности.
   *
   * chi  — позиционный угол светлого края, отсчёт от севера через восток (Меёс 48.5);
   * q    — параллактический угол (наклон «север неба» относительно вертикали);
   * tilt — chi - q: поворот терминатора относительно горизонта у наблюдателя.
   */
  function limbAngles(date, obs) {
    var E = engine();
    var sunEq = E.Equator('Sun', date, obs, true, true);
    var moonEq = E.Equator('Moon', date, obs, true, true);
    var a0 = rad(sunEq.ra * 15), d0 = rad(sunEq.dec);
    var a = rad(moonEq.ra * 15), d = rad(moonEq.dec);

    var chi = Math.atan2(
      Math.cos(d0) * Math.sin(a0 - a),
      Math.sin(d0) * Math.cos(d) - Math.cos(d0) * Math.sin(d) * Math.cos(a0 - a)
    );

    // Параллактический угол: нужен часовой угол Луны в месте наблюдения.
    var gast = E.SiderealTime(date);                 // часы
    var lst = (gast + obs.longitude / 15) * 15;      // градусы
    var H = rad(norm360(lst - moonEq.ra * 15));
    var phi = rad(obs.latitude);
    var q = Math.atan2(Math.sin(H), Math.tan(phi) * Math.cos(d) - Math.sin(d) * Math.cos(H));

    return { chi: norm360(deg(chi)), q: deg(q), tilt: norm360(deg(chi) - deg(q)) };
  }

  // ── Супер/микролуние ────────────────────────────────────────────────────────

  // Долговременные крайние значения расстояния Земля—Луна. Именно ИМИ надо
  // нормировать «супер/микро», а не апсидами текущего месяца: перигей гуляет
  // 356–370 тыс. км от месяца к месяцу, и внутримесячная доля 0.9 может
  // выпасть на 400 тыс. км в один месяц и на 405 тыс. в другой. Тест это и
  // поймал: обычное полнолуние получало ярлык микролуния.
  var MEAN_PERIGEE_KM = 356500;
  var MEAN_APOGEE_KM = 406700;

  /**
   * Где данное расстояние лежит между перигеем и апогеем + ярлыки супер/микро.
   * Порог общепринятый: в пределах 10 % диапазона от перигея — суперлуние,
   * в пределах 10 % от апогея — микролуние (≈361 500 и ≈401 700 км).
   * Даты ближайших апсид отдаём отдельно — они интересны сами по себе.
   */
  function apsisContext(date, distKm) {
    var E = engine();
    var ap = E.SearchLunarApsis(new Date(date.getTime() - 20 * DAY_MS));
    var peri = null, apo = null;
    for (var i = 0; i < 6 && (!peri || !apo); i++) {
      if (ap.kind === 0 /* pericenter */ && !peri) peri = ap;
      if (ap.kind === 1 /* apocenter */ && !apo) apo = ap;
      ap = E.NextLunarApsis(ap);
    }
    var span = MEAN_APOGEE_KM - MEAN_PERIGEE_KM;
    var frac = (distKm - MEAN_PERIGEE_KM) / span;   // 0 = перигей, 1 = апогей
    return {
      perigeeKm: peri ? peri.dist_km : null,
      apogeeKm: apo ? apo.dist_km : null,
      perigeeDate: peri ? peri.time.date : null,
      apogeeDate: apo ? apo.time.date : null,
      fraction: Math.max(0, Math.min(1, frac)),
      isSuper: distKm <= MEAN_PERIGEE_KM + 0.10 * span,
      isMicro: distKm >= MEAN_PERIGEE_KM + 0.90 * span
    };
  }

  // ── Полный снимок Луны ──────────────────────────────────────────────────────

  /**
   * Всё, что нужно странице, одним вызовом и одним согласованным моментом
   * времени. Раздельные вызовы — источник рассогласований («фаза от одного
   * момента, расстояние от другого»), поэтому здесь ровно один date.
   */
  function moonSnapshot(date, lat, lon, elevM) {
    var E = engine();
    var obs = observer(lat, lon, elevM);
    var illum = E.Illumination('Moon', date);
    var phaseDeg = E.MoonPhase(date);
    var lib = E.Libration(date);
    var ecl = E.EclipticGeoMoon(date);
    var hor = E.Horizon(date, obs,
      E.Equator('Moon', date, obs, true, true).ra,
      E.Equator('Moon', date, obs, true, true).dec, 'normal');

    var distKm = lib.dist_km;
    var semiDiamDeg = deg(Math.asin(MOON_RADIUS_KM / distKm));
    var newMoon = previousNewMoon(date);

    return {
      date: date,
      phaseDeg: phaseDeg,
      phaseIndex: phaseIndex(phaseDeg),
      phaseKey: PHASE_KEYS[phaseIndex(phaseDeg)],
      waxing: phaseDeg < 180,
      illumination: illum.phase_fraction,        // 0..1
      magnitude: illum.mag,
      distanceKm: distKm,
      angularDiameterDeg: semiDiamDeg * 2,
      ageDays: newMoon ? (date - newMoon) / DAY_MS : null,
      // EclipticGeoMoon отдаёт Spherical {lat, lon, dist} — НЕ {elon, elat},
      // как Libration. Перепутанные имена молча дают undefined и знак зодиака
      // «undefined» на экране; поймано тестом.
      eclipticLon: norm360(ecl.lon),
      eclipticLat: ecl.lat,
      zodiac: zodiacOf(ecl.lon),
      // elon/elat — ЛИБРАЦИЯ (селенографические координаты подземной точки),
      // ровно то «покачивание» ±8°, из-за которого с Земли видно 59 % поверхности.
      // ⚠️ mlon/mlat движка — это НЕ подсолнечная точка, а геоцентрическая
      // эклиптическая долгота/широта самой Луны (сверено: mlon 151.004 против
      // EclipticGeoMoon.lon 151.006). Подписать их «subsolar» значило бы
      // поставить терминатор не туда — сцена берёт освещение из векторов.
      libration: { lon: lib.elon, lat: lib.elat, diamDeg: lib.diam_deg },
      altitude: hor.altitude,
      azimuth: hor.azimuth,
      limb: limbAngles(date, obs),
      apsis: apsisContext(date, distKm)
    };
  }

  /** Восход / кульминация / заход Луны на местные сутки вокруг date. */
  function moonTimes(date, lat, lon, elevM) {
    var E = engine(), obs = observer(lat, lon, elevM);
    var from = new Date(date.getTime() - DAY_MS / 2);
    var rise = E.SearchRiseSet('Moon', obs, +1, from, 2);
    var set = E.SearchRiseSet('Moon', obs, -1, from, 2);
    var tr = E.SearchHourAngle('Moon', obs, 0, from, +1);
    return {
      rise: rise ? rise.date : null,
      set: set ? set.date : null,
      transit: tr ? tr.time.date : null,
      transitAltitude: tr ? tr.hor.altitude : null
    };
  }

  // ── Геометрия сцены ─────────────────────────────────────────────────────────

  /**
   * Векторы для 3D-сцены «Луна такая, какая она сейчас».
   *
   * Принцип: НЕ подкручиваем сферу «на глазок под фазу», а ставим настоящую
   * геометрию — Земля в начале координат, Луна в своём геоцентрическом векторе,
   * Солнце в своём. Тогда и фаза, и терминатор, и либрация получаются сами, и
   * не могут разойтись между собой. Ориентация тела — из модели вращения МАС
   * (RotationAxis), поэтому к Земле повёрнута ровно та сторона, что и в небе.
   *
   * Возвращает единичные направления в экваториальной системе J2000:
   *   toMoon   — куда смотреть с Земли (камера ставится против него);
   *   toSun    — откуда светит (направление на Солнце ОТ Луны);
   *   northPol — куда смотрит северный полюс Луны;
   *   spinDeg  — угол собственного вращения W вокруг полюса.
   * Плюс distanceKm и (для наблюдателя) угол поворота картинки в небе.
   */
  function moonScene(date, lat, lon, elevM) {
    var E = engine();
    var M = E.GeoVector('Moon', date, true);
    var S = E.GeoVector('Sun', date, true);
    var ax = E.RotationAxis('Moon', date);

    function unit(x, y, z) {
      var n = Math.hypot(x, y, z) || 1;
      return { x: x / n, y: y / n, z: z / n };
    }
    // Полюс задан прямым восхождением (в ЧАСАХ) и склонением — переводим в вектор.
    var raRad = rad(ax.ra * 15), decRad = rad(ax.dec);
    var north = {
      x: Math.cos(decRad) * Math.cos(raRad),
      y: Math.cos(decRad) * Math.sin(raRad),
      z: Math.sin(decRad)
    };

    var out = {
      toMoon: unit(M.x, M.y, M.z),
      toSun: unit(S.x - M.x, S.y - M.y, S.z - M.z),
      northPole: north,
      spinDeg: norm360(ax.spin),
      distanceKm: Math.hypot(M.x, M.y, M.z) * E.KM_PER_AU
    };
    if (typeof lat === 'number' && typeof lon === 'number') {
      out.limb = limbAngles(date, observer(lat, lon, elevM));
    }
    return out;
  }

  // ── Полоса солнечного затмения ──────────────────────────────────────────────

  var EARTH_A_KM = 6378.1366;      // WGS84, экваториальный радиус
  var EARTH_F = 1 / 298.25642;     // сжатие

  /**
   * Центральная линия солнечного затмения — где ось тени протыкает Землю.
   *
   * Зачем своими руками: движок даёт точку НАИБОЛЬШЕГО затмения (одну), а людям
   * нужна полоса — «пройдёт ли она надо мной». Ни один русскоязычный сайт её не
   * рисует, только пересказывает новости.
   *
   * Как: для каждого момента строим прямую от Солнца через Луну и ищем ближнюю
   * точку пересечения с ЭЛЛИПСОИДОМ Земли. Сжатие учитываем масштабированием
   * оси Z — иначе на широте Луксора промах уходит в километры.
   *
   * @param {Date} peak     момент наибольшего затмения (от движка)
   * @param {number} stepMin шаг по времени, мин (2 — гладкая линия)
   * @returns {Array<{lat,lon,date}>} точки центральной линии по времени
   */
  function solarEclipsePath(peak, stepMin) {
    var E = engine();
    var step = (stepMin || 2) * 60000;
    var out = [];
    var zScale = 1 / (1 - EARTH_F);           // z → «сферические» единицы
    var R = EARTH_A_KM / E.KM_PER_AU;

    for (var dt = -3.5 * 3600000; dt <= 3.5 * 3600000; dt += step) {
      var t = new Date(peak.getTime() + dt);
      var rot = E.Rotation_EQJ_EQD(t);
      var M = E.RotateVector(rot, E.GeoVector('Moon', t, true));
      var S = E.RotateVector(rot, E.GeoVector('Sun', t, true));

      // Ось тени: направление от Солнца к Луне и дальше на Землю.
      var dx = M.x - S.x, dy = M.y - S.y, dz = M.z - S.z;
      var n = Math.hypot(dx, dy, dz);
      var ux = dx / n, uy = dy / n, uz = dz / n;

      // В «сферических» координатах: сжимаем Z и решаем |P|=R.
      var mx = M.x, my = M.y, mz = M.z * zScale;
      var vx = ux, vy = uy, vz = uz * zScale;
      var a2 = vx * vx + vy * vy + vz * vz;
      var b2 = 2 * (mx * vx + my * vy + mz * vz);
      var c2 = mx * mx + my * my + mz * mz - R * R;
      var disc = b2 * b2 - 4 * a2 * c2;
      if (disc <= 0) continue;                 // тень мимо Земли — полосы нет
      var s = (-b2 - Math.sqrt(disc)) / (2 * a2);
      if (s <= 0) continue;

      var P = { x: M.x + s * ux, y: M.y + s * uy, z: M.z + s * uz, t: M.t };
      var obs = E.VectorObserver(P, true);
      out.push({ lat: obs.latitude, lon: obs.longitude, date: t });
    }
    return out;
  }

  /**
   * Лунное затмение видно там, где Луна над горизонтом — у Луны нет полосы,
   * есть половина планеты. Возвращаем высоту Луны в момент максимума.
   */
  function lunarEclipseVisibility(peak, lat, lon, elevM) {
    var E = engine(), obs = observer(lat, lon, elevM);
    var eq = E.Equator('Moon', peak, obs, true, true);
    var hor = E.Horizon(peak, obs, eq.ra, eq.dec, 'normal');
    return { altitude: hor.altitude, azimuth: hor.azimuth, visible: hor.altitude > 0 };
  }

  // ── Полоса как ЗОНА, а не линия ─────────────────────────────────────────────

  var SUN_RADIUS_KM = 695700;

  /**
   * ГРАНИЦЫ полосы полной (кольцевой) фазы, а не только центральная линия.
   *
   * Зачем отдельно от solarEclipsePath: линия отвечает «где будет ЛУЧШЕ ВСЕГО»,
   * а человек спрашивает «попадаю ли Я». Между этими вопросами до 130 км в
   * каждую сторону — то есть половина полосы, целые города. Ответить на второй
   * вопрос глядя на линию нельзя: на карте мира она тоньше собственной обводки.
   *
   * Как считаем. Конус тени Луны — настоящий конус: у него есть вершина, и
   * радиус сечения меняется с расстоянием. Берём точку на окружности сечения и
   * ищем, на какой глубине s этот луч протыкает эллипсоид Земли (бисекция по
   * s — надёжнее Ньютона у касательных лучей, где производная вырождается).
   * Обходя окружность, получаем КОНТУР пятна тени на поверхности; крайние
   * точки контура поперёк движения и есть границы полосы.
   *
   * Для кольцевого затмения радиус сечения за вершиной становится
   * отрицательным (антумбра) — берём модуль: геометрия та же, смысл другой.
   *
   * @param {Date} peak момент наибольшего затмения
   * @param {number} stepMin шаг по времени, мин
   * @returns {{center:Array, north:Array, south:Array, widthKm:number|null,
   *            durationSec:number|null, kind:string}}
   */
  function solarEclipseBand(peak, stepMin) {
    var E = engine();
    var step = (stepMin || 4) * 60000;
    var zScale = 1 / (1 - EARTH_F);
    var R = EARTH_A_KM / E.KM_PER_AU;
    var kmPerAu = E.KM_PER_AU;

    var center = [], north = [], south = [], widthAtPeak = null, prevHit = null;

    for (var dt = -4 * 3600000; dt <= 4 * 3600000; dt += step) {
      var t = new Date(peak.getTime() + dt);
      var rot = E.Rotation_EQJ_EQD(t);
      var M = E.RotateVector(rot, E.GeoVector('Moon', t, true));
      var S = E.RotateVector(rot, E.GeoVector('Sun', t, true));

      var dx = M.x - S.x, dy = M.y - S.y, dz = M.z - S.z;
      var dsm = Math.hypot(dx, dy, dz);                 // Солнце → Луна, AU
      var u = { x: dx / dsm, y: dy / dsm, z: dz / dsm };

      var hit = rayHitEllipsoid(M, u, R, zScale);
      if (!hit) { prevHit = null; continue; }           // ось мимо Земли
      var cObs = E.VectorObserver({ x: hit.x, y: hit.y, z: hit.z, t: M.t }, true);
      center.push({ lat: cObs.latitude, lon: cObs.longitude, date: t });

      // Радиус сечения конуса умбры на глубине s (в AU). За вершиной значение
      // отрицательное — это антумбра кольцевого затмения, берём модуль.
      var tanAlpha = (SUN_RADIUS_KM - MOON_RADIUS_KM) / (dsm * kmPerAu);
      var coneRadius = function (sAu) {
        return Math.abs(MOON_RADIUS_KM / kmPerAu - sAu * tanAlpha);
      };

      // Базис поперёк оси.
      var ref = Math.abs(u.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 1, y: 0, z: 0 };
      var e1 = normalize(cross(u, ref));
      var e2 = normalize(cross(u, e1));

      // Контур пятна тени: обходим окружность сечения и для каждого направления
      // ищем глубину, на которой луч конуса протыкает эллипсоид. Бисекция, а не
      // Ньютон: у края пятна луч идёт по касательной, производная там вырождена
      // и Ньютон уходит в космос.
      // 24 направления, а не 48: крайняя точка ищется максимумом проекции, и
      // при шаге 15° её положение ошибается меньше чем на процент ширины —
      // зато полоса считается вдвое быстрее, а это интерактив, где каждый
      // выбор события пересчитывает карту.
      var ring = [], N = 24;
      for (var i = 0; i < N; i++) {
        var th = 2 * Math.PI * i / N;
        var p = coneSurfacePoint(M, u, e1, e2, Math.cos(th), Math.sin(th),
          coneRadius, hit.s, R, zScale);
        if (p) ring.push(p);
      }
      if (ring.length < 6) { prevHit = null; continue; }

      // Поперечное направление: перпендикуляр к движению тени по поверхности.
      var along = prevHit ? sub(hit, prevHit) : u;
      var nrm = normalize(cross({ x: hit.x, y: hit.y, z: hit.z }, along));

      var loD = Infinity, hiD = -Infinity, lo = null, hi = null;
      for (var j = 0; j < ring.length; j++) {
        var p2 = ring[j];
        var d = (p2.x - hit.x) * nrm.x + (p2.y - hit.y) * nrm.y + (p2.z - hit.z) * nrm.z;
        if (d < loD) { loD = d; lo = p2; }
        if (d > hiD) { hiD = d; hi = p2; }
      }
      if (!lo || !hi) { prevHit = hit; continue; }

      var loG = E.VectorObserver({ x: lo.x, y: lo.y, z: lo.z, t: M.t }, true);
      var hiG = E.VectorObserver({ x: hi.x, y: hi.y, z: hi.z, t: M.t }, true);
      north.push({ lat: hiG.latitude, lon: hiG.longitude, date: t });
      south.push({ lat: loG.latitude, lon: loG.longitude, date: t });

      if (widthAtPeak === null || Math.abs(dt) < step / 2) {
        if (Math.abs(dt) < step) {
          widthAtPeak = Math.hypot(hi.x - lo.x, hi.y - lo.y, hi.z - lo.z) * kmPerAu;
        }
      }
      prevHit = hit;
    }

    return {
      center: center, north: north, south: south,
      widthKm: widthAtPeak,
      durationSec: center.length > 1
        ? (center[center.length - 1].date - center[0].date) / 1000 : null
    };
  }

  /** Ближняя точка пересечения луча (p0 + s·u) с эллипсоидом; null — мимо. */
  function rayHitEllipsoid(p0, u, R, zScale) {
    var mx = p0.x, my = p0.y, mz = p0.z * zScale;
    var vx = u.x, vy = u.y, vz = u.z * zScale;
    var a2 = vx * vx + vy * vy + vz * vz;
    var b2 = 2 * (mx * vx + my * vy + mz * vz);
    var c2 = mx * mx + my * my + mz * mz - R * R;
    var disc = b2 * b2 - 4 * a2 * c2;
    if (disc <= 0) return null;
    var s = (-b2 - Math.sqrt(disc)) / (2 * a2);
    if (s <= 0) return null;
    return { s: s, x: p0.x + s * u.x, y: p0.y + s * u.y, z: p0.z + s * u.z };
  }

  /**
   * Точка КОНУСА (не цилиндра) на поверхности эллипсоида в направлении θ.
   * Глубина ищется бисекцией: над поверхностью f>0, под ней f<0.
   */
  function coneSurfacePoint(M, u, e1, e2, ct, st, coneRadius, sCenter, R, zScale) {
    function at(s) {
      var r = coneRadius(s);
      return {
        x: M.x + s * u.x + r * (ct * e1.x + st * e2.x),
        y: M.y + s * u.y + r * (ct * e1.y + st * e2.y),
        z: M.z + s * u.z + r * (ct * e1.z + st * e2.z)
      };
    }
    function f(s) { var p = at(s); return Math.hypot(p.x, p.y, p.z * zScale) - R; }

    // Скобку ИЩЕМ сканированием, а не задаём процентом от центральной глубины.
    // У косой тени (высокие широты) край пятна отстоит от центра на тысячи
    // километров, и узкая скобка молча теряла бо́льшую часть контура: полоса
    // 12.08.2026 строилась пятью точками из двадцати трёх.
    // Диапазон ±10 % от глубины центра — это ±38 тыс. км, то есть заведомо
    // больше диаметра Земли; за ним искать нечего.
    var loS = sCenter * 0.9, hiS = sCenter * 1.1;
    var STEPS = 48, dS = (hiS - loS) / STEPS;
    var prevS = loS, prevF = f(loS);
    var aS = null, bS = null;
    for (var k = 1; k <= STEPS; k++) {
      var curS = loS + k * dS, curF = f(curS);
      if (prevF > 0 && curF <= 0) { aS = prevS; bS = curS; break; }
      prevS = curS; prevF = curF;
    }
    if (aS === null) return null;                      // луч Земли не касается

    // 26 делений сужают скобку в 67 млн раз: от 30 тыс. км до сантиметров.
    // Полсотни итераций, стоявшие здесь, уточняли уже ниже машинной точности.
    for (var b = 0; b < 26; b++) {
      var mid = (aS + bS) / 2;
      if (f(mid) > 0) aS = mid; else bS = mid;
    }
    var p = at((aS + bS) / 2);
    if (Math.abs(Math.hypot(p.x, p.y, p.z * zScale) - R) > 1e-8) return null;
    return p;
  }

  function cross(a, b) {
    return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x };
  }
  function normalize(v) {
    var n = Math.hypot(v.x, v.y, v.z) || 1;
    return { x: v.x / n, y: v.y / n, z: v.z / n };
  }
  function sub(a, b) { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; }
  function geoFromLatLon(p, R, zScale) {
    var la = rad(p.lat), lo = rad(p.lon);
    // Грубая сфера: вектор нужен ТОЛЬКО для направления движения тени, поэтому
    // сжатие здесь роли не играет — направление от него не меняется.
    return { x: R * Math.cos(la) * Math.cos(lo), y: R * Math.cos(la) * Math.sin(lo),
      z: R * Math.sin(la) / zScale };
  }

  // ── Локальные обстоятельства: азимут и магнитуда ────────────────────────────

  /**
   * Куда смотреть и насколько глубоко закрыт диск — в ОДИН момент времени.
   *
   * Движок отдаёт для контактов только высоту (`altitude`) и ни слова про
   * азимут: «Солнце на 12°» не говорит, куда встать, а без открытого горизонта
   * в нужную сторону низкое затмение просто не увидеть. Магнитуды у него тоже
   * нет — а именно её печатают в справочниках, и путать её с закрытой площадью
   * нельзя (при магнитуде 0.5 закрыто ~39 % площади).
   *
   * magnitude = (rs + rm − d) / (2·rs), где d — угловое расстояние центров,
   * rs и rm — видимые угловые радиусы Солнца и Луны В ТОЧКЕ НАБЛЮДАТЕЛЯ
   * (топоцентрические: параллакс Луны до градуса, им нельзя пренебречь).
   */
  function solarDiskGeometry(date, lat, lon, elevM) {
    var E = engine(), obs = observer(lat, lon, elevM);
    var sunEq = E.Equator('Sun', date, obs, true, true);
    var moonEq = E.Equator('Moon', date, obs, true, true);
    var sunHor = E.Horizon(date, obs, sunEq.ra, sunEq.dec, 'normal');
    var moonHor = E.Horizon(date, obs, moonEq.ra, moonEq.dec, 'normal');

    var sunR = deg(Math.asin(SUN_RADIUS_KM / (sunEq.dist * E.KM_PER_AU)));
    var moonR = deg(Math.asin(MOON_RADIUS_KM / (moonEq.dist * E.KM_PER_AU)));

    // Угловое расстояние центров — через скалярное произведение единичных
    // векторов: формула «по катетам» разваливается у полюсов и при малых углах.
    var a1 = rad(sunEq.ra * 15), d1 = rad(sunEq.dec);
    var a2 = rad(moonEq.ra * 15), d2 = rad(moonEq.dec);
    var cosSep = Math.sin(d1) * Math.sin(d2) + Math.cos(d1) * Math.cos(d2) * Math.cos(a1 - a2);
    var sep = deg(Math.acos(Math.max(-1, Math.min(1, cosSep))));

    var mag = (sunR + moonR - sep) / (2 * sunR);

    // Позиционный угол центра Луны относительно центра Солнца, от севера через
    // восток. Сам по себе он рисует картинку «как в атласе»; чтобы получить
    // «как в небе над головой», из него вычитают параллактический угол —
    // наклон небесного меридиана к вертикали наблюдателя. Без этой поправки
    // Луна наползает не с той стороны, и человек ищет тень не там.
    var pa = norm360(deg(Math.atan2(
      Math.cos(d2) * Math.sin(a2 - a1),
      Math.sin(d2) * Math.cos(d1) - Math.cos(d2) * Math.sin(d1) * Math.cos(a2 - a1)
    )));
    var gast = E.SiderealTime(date);
    var lstDeg = (gast + obs.longitude / 15) * 15;
    var H = rad(norm360(lstDeg - sunEq.ra * 15));
    var phi = rad(obs.latitude);
    var qSun = deg(Math.atan2(Math.sin(H),
      Math.tan(phi) * Math.cos(d1) - Math.sin(d1) * Math.cos(H)));

    return {
      sunAltitude: sunHor.altitude, sunAzimuth: sunHor.azimuth,
      moonAltitude: moonHor.altitude, moonAzimuth: moonHor.azimuth,
      sunRadiusDeg: sunR, moonRadiusDeg: moonR,
      separationDeg: sep,
      magnitude: Math.max(0, Math.min(mag, (sunR + moonR) / (2 * sunR))),
      positionAngle: pa,
      parallacticAngle: qSun,
      skyAngle: norm360(pa - qSun)      // от зенита через запад — для картинки
    };
  }

  /**
   * Геометрия ЛУННОГО затмения: где Луна относительно тени Земли.
   *
   * Тень Земли на расстоянии Луны — два круга: умбра (полная тень) и полутень.
   * Их угловые радиусы считаются из подобия конусов, после чего умбра
   * увеличивается на 2 % (поправка Данжона): земная атмосфера рассеивает свет
   * и тень получается больше геометрической. Без поправки моменты U1/U4
   * расходятся с наблюдаемыми на минуты.
   *
   * Центр тени — АНТИСОЛНЕЧНАЯ точка; отсюда и считается расстояние до Луны.
   */
  function lunarShadowGeometry(date, lat, lon, elevM) {
    var E = engine();
    var moon = E.GeoVector('Moon', date, true);
    var sun = E.GeoVector('Sun', date, true);
    var km = E.KM_PER_AU;
    var dMoon = Math.hypot(moon.x, moon.y, moon.z) * km;
    var dSun = Math.hypot(sun.x, sun.y, sun.z) * km;

    var EARTH_R = 6378.1366, SUN_R = SUN_RADIUS_KM;
    var umbraKm = 1.02 * (EARTH_R - dMoon * (SUN_R - EARTH_R) / dSun);
    var penumbraKm = 1.02 * (EARTH_R + dMoon * (SUN_R + EARTH_R) / dSun);

    // Угол Луна—Земля—антисолнечная точка = 180° минус элонгация.
    var cosEl = (moon.x * sun.x + moon.y * sun.y + moon.z * sun.z) /
      (Math.hypot(moon.x, moon.y, moon.z) * Math.hypot(sun.x, sun.y, sun.z));
    var elong = deg(Math.acos(Math.max(-1, Math.min(1, cosEl))));

    var out = {
      separationDeg: 180 - elong,                         // Луна от центра тени
      umbraRadiusDeg: deg(Math.atan2(umbraKm, dMoon)),
      penumbraRadiusDeg: deg(Math.atan2(penumbraKm, dMoon)),
      moonRadiusDeg: deg(Math.asin(MOON_RADIUS_KM / dMoon)),
      distanceKm: dMoon
    };

    // Где всё это в НЕБЕ наблюдателя. Центр земной тени — точка, ровно
    // противоположная Солнцу: её высота зеркальна солнечной, азимут повёрнут
    // на полкруга. Отсюда сцена рисует, с какой стороны в Луну входит тень —
    // а «с какой стороны» человек проверяет глазами первым делом.
    if (typeof lat === 'number' && typeof lon === 'number') {
      var obs = observer(lat, lon, elevM);
      var mEq = E.Equator('Moon', date, obs, true, true);
      var mHor = E.Horizon(date, obs, mEq.ra, mEq.dec, 'normal');
      var sEq = E.Equator('Sun', date, obs, true, true);
      var sHor = E.Horizon(date, obs, sEq.ra, sEq.dec, 'normal');
      out.moonAltitude = mHor.altitude;
      out.moonAzimuth = mHor.azimuth;
      out.shadowAltitude = -sHor.altitude;
      out.shadowAzimuth = norm360(sHor.azimuth + 180);
    }
    return out;
  }

  /**
   * Доля ЗАКРЫТОЙ ПЛОЩАДИ диска по геометрии двух кругов.
   * Именно она определяет, потемнеет ли вокруг; движок даёт её только для
   * своих контактов, а симуляции нужен любой момент между ними.
   */
  function obscurationFrom(sunR, moonR, sep) {
    if (sep >= sunR + moonR) return 0;
    if (sep <= Math.abs(moonR - sunR)) return moonR >= sunR ? 1 : (moonR * moonR) / (sunR * sunR);
    var r1 = sunR, r2 = moonR, d = sep;
    var a1 = Math.acos((d * d + r1 * r1 - r2 * r2) / (2 * d * r1));
    var a2 = Math.acos((d * d + r2 * r2 - r1 * r1) / (2 * d * r2));
    var area = r1 * r1 * (a1 - Math.sin(2 * a1) / 2) + r2 * r2 * (a2 - Math.sin(2 * a2) / 2);
    return area / (Math.PI * r1 * r1);
  }

  // ── Лунное затмение: моменты фаз ────────────────────────────────────────────

  /**
   * P1/U1/U2 — максимум — U3/U4/P4 по полудлительностям движка.
   *
   * Движок отдаёт только пик и ПОЛУдлительности в минутах (sd_penum, sd_partial,
   * sd_total) — то есть «фаза длится 338 минут», и человек сам вычитает половину
   * из максимума, чтобы понять, во сколько выходить смотреть. Считаем за него:
   * лунное затмение видно с половины планеты, и вопрос там не «видно ли», а
   * «во сколько именно».
   */
  function lunarEclipseContacts(peak, sdPenum, sdPartial, sdTotal) {
    var t = peak.getTime(), M = 60000;
    var out = { peak: peak };
    if (sdPenum) { out.p1 = new Date(t - sdPenum * M); out.p4 = new Date(t + sdPenum * M); }
    if (sdPartial) { out.u1 = new Date(t - sdPartial * M); out.u4 = new Date(t + sdPartial * M); }
    if (sdTotal) { out.u2 = new Date(t - sdTotal * M); out.u3 = new Date(t + sdTotal * M); }
    return out;
  }

  return {
    PHASE_KEYS: PHASE_KEYS,
    ZODIAC: ZODIAC,
    moonScene: moonScene,
    solarEclipsePath: solarEclipsePath,
    solarEclipseBand: solarEclipseBand,
    solarDiskGeometry: solarDiskGeometry,
    lunarShadowGeometry: lunarShadowGeometry,
    obscurationFrom: obscurationFrom,
    lunarEclipseContacts: lunarEclipseContacts,
    lunarEclipseVisibility: lunarEclipseVisibility,
    observer: observer,
    phaseIndex: phaseIndex,
    nextQuarters: nextQuarters,
    previousNewMoon: previousNewMoon,
    lunarDay: lunarDay,
    zodiacOf: zodiacOf,
    limbAngles: limbAngles,
    apsisContext: apsisContext,
    moonSnapshot: moonSnapshot,
    moonTimes: moonTimes,
    _deg: deg, _rad: rad, _norm360: norm360
  };
}));
